import React, { useState, useMemo } from 'react';
import {
  Box, Typography, FormControl, FormControlLabel, RadioGroup, Radio,
  TextField, Select, MenuItem, InputLabel, Switch, Alert, Paper,
  Grid, Chip, Button, Divider, InputAdornment
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Repeat as RepeatIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
// Plain date-fns on purpose: the next-run preview is computed as local
// wall-clock Dates and labelled with the schedule's own timezone, so
// the user-timezone-aware format from utils/datetime would mislabel it.
import { format, addDays, addHours } from 'date-fns';

// Common cron expressions
const COMMON_SCHEDULES = [
  { label: 'Every hour', value: '0 */1 * * *', description: 'Runs at the start of every hour' },
  { label: 'Every 6 hours', value: '0 */6 * * *', description: 'Runs every 6 hours starting at midnight' },
  { label: 'Daily at 2 AM', value: '0 2 * * *', description: 'Runs once per day at 2:00 AM' },
  { label: 'Daily at midnight', value: '0 0 * * *', description: 'Runs once per day at midnight' },
  { label: 'Weekly on Sunday', value: '0 0 * * 0', description: 'Runs every Sunday at midnight' },
  { label: 'Monthly on 1st', value: '0 0 1 * *', description: 'Runs on the 1st day of each month' }
];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' }
];

const ScheduleBuilder = ({ schedule = {}, onChange }) => {
  
  // Derive all values from props
  const scheduleType = schedule.type || 'once';
  const cronExpression = schedule.cron_expression || '0 2 * * *';
  const intervalSeconds = schedule.interval_seconds || 3600;
  const timezone = schedule.timezone || 'UTC';
  const enabled = schedule.enabled !== false;
  
  // Local state only for validation
  const [cronError, setCronError] = useState('');
  
  // Note: getNextRuns is defined after the handlers, so we'll calculate nextRuns later

  const handleScheduleTypeChange = (event) => {
    const newType = event.target.value;
    
    // Only update if the type actually changed
    if (newType === scheduleType) {
      return;
    }
    
    setCronError('');
    
    const newSchedule = {
      ...schedule,
      type: newType,
      enabled,
      timezone
    };

    // Clean up fields based on type
    if (newSchedule.type === 'once') {
      delete newSchedule.cron_expression;
      delete newSchedule.interval_seconds;
    } else if (newSchedule.type === 'cron') {
      newSchedule.cron_expression = cronExpression;
      delete newSchedule.interval_seconds;
    } else if (newSchedule.type === 'interval') {
      newSchedule.interval_seconds = intervalSeconds;
      delete newSchedule.cron_expression;
    }

    onChange(newSchedule);
  };

  const handleCronExpressionChange = (value) => {
    validateCronExpression(value);
    const newSchedule = {
      ...schedule,
      cron_expression: value
    };
    onChange(newSchedule);
  };

  const validateCronExpression = (expr) => {
    // Basic cron validation (5 fields: minute hour day month dayOfWeek)
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) {
      setCronError('Cron expression must have exactly 5 fields: minute hour day month dayOfWeek');
      return false;
    }
    
    setCronError('');
    return true;
  };
  
  // Pure validation function that doesn't set state
  const isValidCronExpression = (expr) => {
    const parts = expr.trim().split(/\s+/);
    return parts.length === 5;
  };

  const handleIntervalChange = (value) => {
    const seconds = Math.max(10, parseInt(value) || 60); // Minimum 10 seconds
    const newSchedule = {
      ...schedule,
      interval_seconds: seconds
    };
    onChange(newSchedule);
  };
  
  const handleTimezoneChange = (value) => {
    const newSchedule = {
      ...schedule,
      timezone: value
    };
    onChange(newSchedule);
  };
  
  const handleEnabledChange = (value) => {
    const newSchedule = {
      ...schedule,
      enabled: value
    };
    onChange(newSchedule);
  };

  const getNextRuns = () => {
    const now = new Date();
    const runs = [];
    
    if (scheduleType === 'cron' && isValidCronExpression(cronExpression)) {
      // For demo purposes, show approximate next runs
      // In a real implementation, you'd use a proper cron parser
      if (cronExpression === '0 2 * * *') { // Daily at 2 AM
        for (let i = 0; i < 5; i++) {
          const nextRun = addDays(now, i);
          nextRun.setHours(2, 0, 0, 0);
          if (nextRun > now) {
            runs.push(nextRun);
          }
        }
      } else if (cronExpression === '0 */6 * * *') { // Every 6 hours
        let nextRun = new Date(now);
        nextRun.setMinutes(0, 0, 0);
        nextRun.setHours(Math.ceil(now.getHours() / 6) * 6);
        
        for (let i = 0; i < 5; i++) {
          runs.push(new Date(nextRun));
          nextRun = addHours(nextRun, 6);
        }
      }
    } else if (scheduleType === 'interval') {
      let nextRun = new Date(now.getTime() + intervalSeconds * 1000);
      for (let i = 0; i < 5; i++) {
        runs.push(new Date(nextRun));
        nextRun = new Date(nextRun.getTime() + intervalSeconds * 1000);
      }
    }
    
    return runs.slice(0, 5);
  };

  const formatInterval = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0) parts.push(`${remainingSeconds}s`);
    
    return parts.join(' ') || '0s';
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <ScheduleIcon sx={{ mr: 1 }} />
        Schedule Configuration
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure when and how often this import job should run
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset">
            <RadioGroup value={scheduleType} onChange={handleScheduleTypeChange}>
              <FormControlLabel 
                value="once" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body1">Run Once</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Manual trigger only
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel 
                value="cron" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body1">Schedule (Cron)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use cron expressions for complex schedules
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel 
                value="interval" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body1">Repeat Interval</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Run every X seconds/minutes/hours
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {scheduleType === 'cron' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Common Schedules
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {COMMON_SCHEDULES.map((schedule) => (
                  <Chip
                    key={schedule.value}
                    label={schedule.label}
                    onClick={() => handleCronExpressionChange(schedule.value)}
                    variant={cronExpression === schedule.value ? 'filled' : 'outlined'}
                    size="small"
                    clickable
                  />
                ))}
              </Box>
              
              <TextField
                fullWidth
                label="Cron Expression"
                value={cronExpression}
                onChange={(e) => handleCronExpressionChange(e.target.value)}
                error={!!cronError}
                helperText={cronError || 'Format: minute hour day month dayOfWeek (e.g., 0 2 * * * for daily at 2 AM)'}
                placeholder="0 2 * * *"
                size="small"
              />

              <FormControl fullWidth sx={{ mt: 2 }} size="small">
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone}
                  onChange={(e) => handleTimezoneChange(e.target.value)}
                  label="Timezone"
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {scheduleType === 'interval' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Quick Intervals
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip 
                  label="10 seconds" 
                  onClick={() => handleIntervalChange(10)}
                  variant={intervalSeconds === 10 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="30 seconds" 
                  onClick={() => handleIntervalChange(30)}
                  variant={intervalSeconds === 30 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="1 minute" 
                  onClick={() => handleIntervalChange(60)}
                  variant={intervalSeconds === 60 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="5 minutes" 
                  onClick={() => handleIntervalChange(300)}
                  variant={intervalSeconds === 300 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="15 minutes" 
                  onClick={() => handleIntervalChange(900)}
                  variant={intervalSeconds === 900 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="30 minutes" 
                  onClick={() => handleIntervalChange(1800)}
                  variant={intervalSeconds === 1800 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="1 hour" 
                  onClick={() => handleIntervalChange(3600)}
                  variant={intervalSeconds === 3600 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="6 hours" 
                  onClick={() => handleIntervalChange(21600)}
                  variant={intervalSeconds === 21600 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="12 hours" 
                  onClick={() => handleIntervalChange(43200)}
                  variant={intervalSeconds === 43200 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
                <Chip 
                  label="Daily" 
                  onClick={() => handleIntervalChange(86400)}
                  variant={intervalSeconds === 86400 ? 'filled' : 'outlined'}
                  size="small"
                  clickable
                />
              </Box>
              
              <TextField
                fullWidth
                label="Custom Interval (seconds)"
                type="text"
                value={intervalSeconds}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
                  if (value !== '') {
                    handleIntervalChange(value);
                  }
                }}
                helperText={`Runs every ${formatInterval(intervalSeconds)} (minimum: 10 seconds)`}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                }}
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => handleEnabledChange(e.target.checked)}
                color="primary"
              />
            }
            label="Schedule Enabled"
            sx={{ mt: 2 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon sx={{ mr: 1, fontSize: 18 }} />
              Schedule Preview
            </Typography>
            
            {scheduleType === 'once' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This job will only run when triggered manually
              </Alert>
            )}
            
            {!enabled && scheduleType !== 'once' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Schedule is disabled. Job will only run when triggered manually.
              </Alert>
            )}
            
            {enabled && scheduleType !== 'once' && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Next 5 scheduled runs:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {(() => {
                    const runs = getNextRuns();
                    if (runs.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Unable to preview schedule
                        </Typography>
                      );
                    }
                    return runs.map((run, index) => (
                      <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                        {format(run, 'MMM dd, yyyy HH:mm:ss')} {timezone !== 'UTC' && `(${timezone})`}
                      </Typography>
                    ));
                  })()}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScheduleBuilder;